import { useState } from 'react';
import { useArchivo, useSubida } from '@plataforma/web/api';

/**
 * Imágenes públicas (arquitectura 2.19): el lotero sube un archivo desde su teléfono o su
 * computadora (nada de pegar direcciones); Media & Documents lo optimiza y Commerce guarda solo
 * su identificador. Las direcciones https capturadas antes se muestran y se conservan.
 */
export type UsoImagen = 'logo' | 'portada' | 'galeria' | 'publicacion';

const ACEPTA = 'image/jpeg,image/png,image/webp,image/heic,image/heif';
const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/** `/medios/<lotero>/<id>` → `<id>`; un identificador o una dirección https quedan igual. */
export const valorImagen = (v: string) => {
  const id = v.startsWith('/medios/') ? v.split('/').pop() : v;
  return id && UUID.test(id) ? id.toLowerCase() : v;
};

/** Vista previa: la imagen propia se pide con la sesión (aún puede no ser pública). */
export function VistaImagen({
  valor,
  alto = 96,
  ajuste = 'cover',
}: {
  valor: string;
  alto?: number;
  ajuste?: 'cover' | 'contain';
}) {
  const propia = UUID.test(valor);
  const local = useArchivo(propia ? `/inventory/imagenes/${valor}/miniatura.webp` : null);
  const src = propia ? local : valor;
  return (
    <span
      style={{
        display: 'inline-grid',
        placeItems: 'center',
        height: alto,
        aspectRatio: ajuste === 'contain' ? '1' : '4 / 3',
        borderRadius: 10,
        overflow: 'hidden',
        background: 'var(--superficie-2, #f1f1f4)',
        border: '1px solid var(--borde, #e5e5ea)',
      }}
    >
      {src ? (
        <img
          src={src}
          alt=""
          style={{ width: '100%', height: '100%', objectFit: ajuste, display: 'block' }}
        />
      ) : (
        <small className="subtitulo">Cargando…</small>
      )}
    </span>
  );
}

function useSubirImagen(uso: UsoImagen) {
  const subir = useSubida();
  const [subiendo, setSubiendo] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const subirArchivos = async (archivos: File[]) => {
    setError(null);
    const ids: string[] = [];
    setSubiendo(archivos.length);
    for (const a of archivos) {
      try {
        ids.push((await subir<{ id: string }>(`/inventory/imagenes?uso=${uso}`, a)).id);
      } catch (e) {
        setError(`${a.name}: ${(e as Error).message}`);
      }
      setSubiendo((n) => n - 1);
    }
    return ids;
  };
  return { subirArchivos, subiendo, error };
}

/**
 * Una sola imagen (logotipo, portada o foto de la identidad, como la de cada persona del equipo). Escribe en el formulario `<nombre>` con el identificador
 * y, si sigue la dirección de antes, `<nombre legado>` con ella.
 */
export function CampoImagen({
  uso,
  rotulo,
  ayuda,
  nombre,
  nombreLegado,
  inicial,
  legado,
}: {
  uso: 'logo' | 'portada' | 'galeria';
  rotulo: string;
  ayuda: string;
  nombre: string;
  nombreLegado: string;
  inicial: string | null | undefined;
  legado: string | null | undefined;
}) {
  const [valor, setValor] = useState<string | null>(inicial ?? legado ?? null);
  const { subirArchivos, subiendo, error } = useSubirImagen(uso);
  const propia = valor !== null && UUID.test(valor);
  return (
    <div className="campo">
      <span>{rotulo}</span>
      <div style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
        {valor && <VistaImagen valor={valor} ajuste={uso === 'logo' ? 'contain' : 'cover'} />}
        <label className="boton">
          {subiendo ? 'Subiendo…' : valor ? 'Cambiar' : 'Subir imagen'}
          <input
            type="file"
            accept={ACEPTA}
            hidden
            disabled={subiendo > 0}
            onChange={(e) => {
              const archivos = Array.from(e.target.files ?? []);
              e.target.value = '';
              void subirArchivos(archivos).then(([id]) => id && setValor(id));
            }}
          />
        </label>
        {valor && (
          <button type="button" className="boton" onClick={() => setValor(null)}>
            Quitar
          </button>
        )}
      </div>
      <small className="subtitulo">{ayuda}</small>
      {error && <small className="error">{error}</small>}
      <input type="hidden" name={nombre} value={propia ? valor : ''} />
      <input type="hidden" name={nombreLegado} value={valor && !propia ? valor : ''} />
    </div>
  );
}

/**
 * Varias imágenes en orden (galería de la tienda o fotos de una publicación). Sin `nombre` no
 * escribe en un formulario y avisa cada cambio con `alCambiar`.
 */
export function CampoGaleria({
  uso,
  rotulo,
  ayuda,
  nombre,
  inicial,
  maximo,
  alCambiar,
}: {
  uso: 'galeria' | 'publicacion';
  rotulo: string;
  ayuda: string;
  nombre?: string;
  inicial: readonly string[];
  maximo: number;
  alCambiar?: (valores: string[]) => void;
}) {
  const [valores, setValores] = useState<string[]>(() => inicial.map(valorImagen));
  const { subirArchivos, subiendo, error } = useSubirImagen(uso);
  const poner = (nuevos: string[]) => {
    setValores(nuevos);
    alCambiar?.(nuevos);
  };
  const mover = (i: number, a: number) => {
    const n = [...valores];
    const [x] = n.splice(i, 1);
    if (x) n.splice(a, 0, x);
    poner(n);
  };
  return (
    <div className="campo">
      <span>
        {rotulo} ({valores.length} de {maximo})
      </span>
      <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'flex-start' }}>
        {valores.map((v, i) => (
          <div key={v} style={{ display: 'grid', gap: 4, justifyItems: 'center' }}>
            <VistaImagen valor={v} alto={84} />
            <div style={{ display: 'flex', gap: 4 }}>
              {i > 0 && (
                <button
                  type="button"
                  className="boton"
                  aria-label="Mover antes"
                  onClick={() => mover(i, i - 1)}
                >
                  ←
                </button>
              )}
              <button
                type="button"
                className="boton"
                aria-label="Quitar"
                onClick={() => poner(valores.filter((x) => x !== v))}
              >
                ✕
              </button>
            </div>
            {i === 0 && <small className="subtitulo">Principal</small>}
          </div>
        ))}
        {valores.length < maximo && (
          <label className="boton" style={{ alignSelf: 'center' }}>
            {subiendo ? `Subiendo ${subiendo}…` : 'Agregar fotos'}
            <input
              type="file"
              accept={ACEPTA}
              multiple
              hidden
              disabled={subiendo > 0}
              onChange={(e) => {
                const archivos = Array.from(e.target.files ?? []).slice(0, maximo - valores.length);
                e.target.value = '';
                void subirArchivos(archivos).then(
                  (ids) => ids.length && poner([...valores, ...ids]),
                );
              }}
            />
          </label>
        )}
      </div>
      <small className="subtitulo">{ayuda}</small>
      {error && <small className="error">{error}</small>}
      {nombre && <input type="hidden" name={nombre} value={JSON.stringify(valores)} />}
    </div>
  );
}
